/**
 * SharedFiltersProvider — Phase 5.0.
 * Owns the cross-module dashboard filter state (team_name, managers, assignees)
 * + its load status. Extracted from FintechContextProvider.
 *
 * The actual filter-calculation effect that reads from each dataset still lives
 * in the legacy FintechContextProvider during Phase 5; it will migrate to this
 * provider (or a hook that reads from domain providers) in Phase 6.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { DashboardFilters } from '../../fintech-ops-dashboard/FinTechOpsDataInterfaces';

export type FiltersStatus = 'pending' | 'loading' | 'finished' | 'error';

/**
 * Shared, in-session filter selections for the SIM ticket-health tabs. Single source of
 * truth lives in simHealthAggregations (next to matchesSimSegment / applySimTicketFilters);
 * re-exported here so context consumers can import it from the provider too.
 */
export type { SimTicketFilterSelections } from 'src/components/fintech-ops/sim-ticket-health-preview/utils/simHealthAggregations';
import type {
  SimTicketFilterSelections,
  DateRangePickerValue
} from 'src/components/fintech-ops/sim-ticket-health-preview/utils/simHealthAggregations';
import moment from 'moment-timezone';

/**
 * Default Ticket Creation Date Range = current + prior month, up to TODAY (never the future).
 * Computed as an absolute range: start of the previous calendar month → today. End is capped at
 * today because tickets can't be created in the future and the current month is only partial.
 * Absolute (not relative) so it reads as a concrete window; recomputed on each app load.
 */
const defaultCreatedDateRange = () => {
  const now = moment();
  return {
    type: 'absolute' as const,
    startDate: now.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
    endDate: now.clone().format('YYYY-MM-DD')
  };
};

const EMPTY_SIM_TICKET_FILTERS: SimTicketFilterSelections = {
  managers: [],
  applications: [],
  severities: [],
  teams: [],
  assignees: [],
  resolutionTypes: [],
  statuses: [],
  months: [],
  createdDateRange: null,
  segment: null
};

export interface SharedFiltersContextValue {
  dashboardFilterData: DashboardFilters;
  setDashboardFilterData: (filters: DashboardFilters) => void;

  dashboardFilterStatus: FiltersStatus;
  setDashboardFilterStatus: (status: FiltersStatus) => void;

  dashboardFilterDataCalculationStatus: FiltersStatus;
  setDashboardFilterDataCalculationStatus: (status: FiltersStatus) => void;

  simTicketFilters: SimTicketFilterSelections;
  setSimTicketFilters: (filters: SimTicketFilterSelections | ((prev: SimTicketFilterSelections) => SimTicketFilterSelections)) => void;
  /** The created-date range auto-seeded on mount. Consumers compare against simTicketFilters
   *  .createdDateRange to tell a user-picked range apart from the default (e.g. to gate LLM calls). */
  seededCreatedDateRange: DateRangePickerValue;
}

const INITIAL_FILTERS = {} as DashboardFilters;

const defaultValue: SharedFiltersContextValue = {
  dashboardFilterData: INITIAL_FILTERS,
  setDashboardFilterData: () => {},
  dashboardFilterStatus: 'loading',
  setDashboardFilterStatus: () => {},
  dashboardFilterDataCalculationStatus: 'loading',
  setDashboardFilterDataCalculationStatus: () => {},
  simTicketFilters: EMPTY_SIM_TICKET_FILTERS,
  setSimTicketFilters: () => {},
  seededCreatedDateRange: defaultCreatedDateRange()
};

const SharedFiltersContext = createContext<SharedFiltersContextValue>(defaultValue);

export const useSharedFilters = () => useContext(SharedFiltersContext);

interface Props {
  children: React.ReactNode;
}

export const SharedFiltersProvider = ({ children }: Props) => {
  const [dashboardFilterData, setDashboardFilterDataState] = useState<DashboardFilters>(INITIAL_FILTERS);
  const [dashboardFilterStatus, setDashboardFilterStatusState] = useState<FiltersStatus>('loading');
  const [dashboardFilterDataCalculationStatus, setDashboardFilterDataCalculationStatusState] = useState<FiltersStatus>('loading');
  // Compute the default created-date range LAZILY in the initializer (not frozen at module import),
  // so a long-lived SPA session that crosses a day/month boundary still seeds a fresh range on
  // first mount rather than reusing a stale module-scope constant. EMPTY_SIM_TICKET_FILTERS keeps
  // createdDateRange: null so the "seeded default vs user-applied" distinction stays clean.
  // The same seeded value is retained in a ref so consumers can tell a user-picked range apart from
  // the auto-seeded default (used to gate live LLM insight calls on genuine user interaction).
  const seededCreatedDateRangeRef = useRef<DateRangePickerValue>(defaultCreatedDateRange());
  const [simTicketFilters, setSimTicketFiltersState] = useState<SimTicketFilterSelections>(() => ({
    ...EMPTY_SIM_TICKET_FILTERS,
    createdDateRange: seededCreatedDateRangeRef.current
  }));

  const setDashboardFilterData = useCallback((f: DashboardFilters) => setDashboardFilterDataState(f), []);
  const setDashboardFilterStatus = useCallback((s: FiltersStatus) => setDashboardFilterStatusState(s), []);
  const setDashboardFilterDataCalculationStatus = useCallback((s: FiltersStatus) => setDashboardFilterDataCalculationStatusState(s), []);
  const setSimTicketFilters = useCallback(
    (f: SimTicketFilterSelections | ((prev: SimTicketFilterSelections) => SimTicketFilterSelections)) => setSimTicketFiltersState(f),
    []
  );

  const value = useMemo(
    () => ({
      dashboardFilterData,
      setDashboardFilterData,
      dashboardFilterStatus,
      setDashboardFilterStatus,
      dashboardFilterDataCalculationStatus,
      setDashboardFilterDataCalculationStatus,
      simTicketFilters,
      setSimTicketFilters,
      seededCreatedDateRange: seededCreatedDateRangeRef.current
    }),
    [
      dashboardFilterData,
      setDashboardFilterData,
      dashboardFilterStatus,
      setDashboardFilterStatus,
      dashboardFilterDataCalculationStatus,
      setDashboardFilterDataCalculationStatus,
      simTicketFilters,
      setSimTicketFilters
    ]
  );

  return <SharedFiltersContext.Provider value={value}>{children}</SharedFiltersContext.Provider>;
};
